import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router, Routes, Route, useParams, Link, useNavigate } from 'react-router-dom';
import {
  Maximize2, ThumbsUp, ThumbsDown, RotateCcw,
  ExternalLink, Info, Gamepad, Play,
  Gamepad2, Search, Menu, Dice5, Heart,
  ShieldCheck, Zap, Star, Flame, AlertCircle
} from 'lucide-react';
import { GAMES, ADSENSE_CLIENT_ID } from './constants';

// --- 核心广告组件 (AdUnit) ---
const AdUnit: React.FC<{ slot: string; format?: string; className?: string }> = ({ slot, format = 'auto', className }) => {
  useEffect(() => {
    try {
      const adsbygoogle = (window as any).adsbygoogle || [];
      adsbygoogle.push({});
    } catch (e) {
      console.error("AdSense Error:", e);
    }
  }, []);
  return (
    <div className={`ad-container flex flex-col items-center justify-center overflow-hidden w-full ${className || ''}`}>
      <div className="text-[10px] text-gray-600 mb-1 uppercase tracking-widest">Advertisement</div>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%' }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      ></ins>
    </div>
  );
};

// --- 导航栏组件 (Navbar) ---
const Navbar = () => {
  const navigate = useNavigate();
  return (
    <nav className="sticky top-0 z-50 bg-gaming-900/95 backdrop-blur-md border-b border-gaming-700 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="p-2 bg-gradient-to-tr from-purple-600 to-cyan-500 rounded-lg group-hover:rotate-12 transition-transform">
            <Gamepad2 className="text-white h-6 w-6" />
          </div>
          <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
            Nexus Games
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className="text-sm text-gray-300 hover:text-cyan-400 transition-colors">Home</Link>
          <button
            onClick={() => navigate(`/play/${GAMES[Math.floor(Math.random() * GAMES.length)].id}`)}
            className="flex items-center gap-2 bg-gaming-800 px-3 py-1.5 rounded-full border border-gaming-700 hover:border-cyan-400/50 text-sm"
          >
            <Dice5 size={16} className="text-cyan-400" />
            <span>Random Game</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

// --- 游戏卡片组件 (GameCard) ---
const GameCard: React.FC<{ game: any }> = ({ game }) => (
  <Link to={`/play/${game.id}`} className="group relative bg-gaming-800 rounded-xl overflow-hidden hover:scale-105 transition-all border border-gaming-700 hover:border-cyan-400 shadow-xl flex flex-col">
    <div className="relative aspect-video">
      <img src={game.thumbnail} alt={game.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
        <Play fill="white" className="text-white" size={48} />
      </div>
    </div>
    <div className="p-4">
      <h3 className="font-bold text-white group-hover:text-cyan-400 transition-colors">{game.title}</h3>
      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1 text-yellow-500 font-bold"><Star size={12} fill="currentColor" /> {game.rating}</span>
        <span className="flex items-center gap-1"><ShieldCheck size={12} /> Unblocked</span>
      </div>
    </div>
  </Link>
);

// --- 游戏播放器页面 (GamePlay) ---
const GamePlayPage = () => {
  const { id } = useParams();
  const game = GAMES.find(g => g.id === id);
  const [isPlaying, setIsPlaying] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleReload = () => {
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  useEffect(() => {
    if (game) document.title = `Play ${game.title} - Unblocked Games 2025`;
    window.scrollTo(0, 0);
    setIsPlaying(false);
  }, [game, id]);

  const handleFullscreen = () => {
    if (document.documentElement.requestFullscreen) document.documentElement.requestFullscreen();
  };

  useEffect(() => {
    const handleF = (e: KeyboardEvent) => e.key.toLowerCase() === 'f' && handleFullscreen();
    window.addEventListener('keydown', handleF);
    return () => window.removeEventListener('keydown', handleF);
  }, []);

  if (!game) return <div className="p-20 text-center">Game Not Found</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* 顶部广告位 */}
      <AdUnit slot="top-banner-ad" className="mb-8" />

      {/* 移动适配提示 */}
      <div className="text-center text-white bg-purple-800 p-3 rounded-lg mb-6 max-w-xl mx-auto shadow-lg border border-purple-600">
        📱 Mobile Tip: Rotate to landscape for the best experience on Chromebooks and phones!
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-9">
          <h1 className="text-3xl font-extrabold text-white mb-6 flex items-center gap-3">
            {game.title}
            <span className="text-xs bg-cyan-500 text-gaming-900 px-2 py-1 rounded-full uppercase tracking-tighter">Verified 2025</span>
          </h1>

          {/* 全屏按钮 */}
          <button
            onClick={handleFullscreen}
            className="block mx-auto w-full md:w-auto bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-12 rounded-xl text-xl mb-6 shadow-[0_0_20px_rgba(22,163,74,0.4)] transition-all transform hover:scale-105"
          >
            Play Full Screen (Press F - Best Experience!)
          </button>

          {/* 加载提示 */}
          <div className="text-center text-gray-400 mb-4 text-sm font-medium flex items-center justify-center gap-2">
            <Zap size={14} className="text-yellow-500" />
            Loading game assets... (Zero lag on 2025 high-speed networks)
          </div>

          {/* 游戏容器 */}
          <div className="relative aspect-video bg-black rounded-2xl overflow-hidden border border-gaming-700 shadow-2xl">
            {!isPlaying ? (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center cursor-pointer group" onClick={() => setIsPlaying(true)}>
                <img src={game.thumbnail} className="absolute inset-0 w-full h-full object-cover opacity-40 blur-sm group-hover:blur-0 transition-all" />
                <div className="relative z-20 bg-cyan-500 text-gaming-900 p-6 rounded-full shadow-[0_0_30px_rgba(6,182,212,0.6)] group-hover:scale-110 transition-transform">
                  <Play fill="currentColor" size={48} />
                </div>
                <p className="relative z-20 mt-6 text-2xl font-bold text-white drop-shadow-lg">Click to Launch Game</p>
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                src={game.embedUrl}
                className="w-full h-full border-0"
                allowFullScreen
                title={game.title}
              />
            )}
          </div>

          {/* 控制栏 */}
          <div className="mt-4 bg-gaming-800 p-4 rounded-xl flex items-center justify-between border border-gaming-700">
            <div className="flex gap-4">
              <button className="flex items-center gap-2 text-gray-400 hover:text-green-400 transition-colors"><ThumbsUp size={20} /> Like</button>
              <button className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors"><ThumbsDown size={20} /></button>
            </div>
            <button onClick={handleReload} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"><RotateCcw size={18} /> Reload</button>
          </div>

          {/* 底部广告位 */}
          <div className="my-10 flex justify-center">
            <AdUnit slot="game-bottom-ad" className="min-h-[250px]" />
          </div>

          {/* 原创攻略内容（修复低价值） */}
          <div className="bg-gaming-800/50 rounded-2xl p-8 border border-gaming-700 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">Ultimate Unblocked Games Hub 2026</h2>
              <p className="text-gray-300 leading-relaxed text-lg">
                Welcome to the best unblocked games collection for 2026! This hub features hundreds of HTML5 games that work on school Chromebooks, restricted networks, and mobile devices. No downloads, no Flash, just instant play with zero lag.
              </p>
              <p className="text-gray-300 leading-relaxed text-lg">
                Our games are carefully curated for maximum fun during breaks. From classic retro titles to modern 3D experiences, we bypass most filters while keeping load times under 5 seconds. Bookmark this site for daily gaming freedom!
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">Pro Tips for Best Experience</h2>
              <ul className="list-disc pl-6 text-gray-300 space-y-2">
                <li>Press F for fullscreen mode - perfect immersion on any device.</li>
                <li>Rotate to landscape on mobile for optimal controls.</li>
                <li>Use Chrome browser for fastest loading and smoothest gameplay.</li>
                <li>Try random game button for surprise discoveries!</li>
              </ul>
            </section>
          </div>

          {/* 内链列表 */}
          <div className="mt-8 bg-gray-900/50 p-6 rounded-xl border border-gray-800">
            <h3 className="text-xl font-bold text-white mb-4">More Unblocked Games 2026</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <li><a href="https://nodownload2025.online" className="text-cyan-500 hover:underline">No Download Hub 2026</a></li>
              <li><a href="https://freegames2025.sbs" className="text-cyan-500 hover:underline">Free Games Portal</a></li>
              <li><a href="https://retrobowl2025.online" className="text-cyan-500 hover:underline">Retro Bowl Football</a></li>
              <li><a href="https://1v1lol2025.online" className="text-cyan-500 hover:underline">1v1.LOL Building Shooter</a></li>
            </ul>
          </div>
        </div>

        {/* 侧边栏 */}
        <aside className="lg:col-span-3 space-y-8">
          <div className="bg-gaming-950 p-6 rounded-2xl border border-gaming-800 sticky top-24">
            <h4 className="font-bold text-white mb-4 flex items-center gap-2"><Flame size={18} className="text-orange-500" /> Hot Suggestions</h4>
            <div className="space-y-4">
              {GAMES.filter(g => g.id !== id).slice(0, 5).map(g => (
                <Link key={g.id} to={`/play/${g.id}`} className="flex gap-3 group">
                  <img src={g.thumbnail} className="w-20 h-14 object-cover rounded-lg border border-gray-700 group-hover:border-cyan-400 transition-colors" />
                  <div className="overflow-hidden">
                    <h5 className="text-sm font-bold text-white truncate group-hover:text-cyan-400 transition-colors">{g.title}</h5>
                    <span className="text-[10px] text-gray-500 uppercase">{g.category}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <AdUnit slot="sidebar-ad" format="rectangle" className="min-h-[300px]" />
        </aside>
      </div>
    </div>
  );
};

// --- 首页组件 (Home) ---
const Home = () => (
  <div className="max-w-7xl mx-auto px-4 py-12">
    <div className="text-center mb-16">
      <h1 className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight">
        Unblocked Games <span className="text-cyan-500">2026</span>
      </h1>
      <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
        The ultimate station for free, school-friendly HTML5 games. No downloads, zero lag, and 100% unblocked in 2026.
      </p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
      {GAMES.map(game => <GameCard key={game.id} game={game} />)}
    </div>
  </div>
);

// --- 主应用组件 (App) ---
export default function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gaming-900 text-gray-100 font-sans">
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/play/:id" element={<GamePlayPage />} />
          </Routes>
        </main>
        <footer className="mt-20 border-t border-gaming-800 py-12 bg-gaming-950">
          <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <p className="font-bold text-white text-lg mb-2">Nexus Unblocked Games 2026</p>
              <p className="text-gray-500 text-sm">Play free, no-flash HTML5 games anywhere, anytime.</p>
            </div>
            <div className="flex gap-8 text-sm text-gray-400">
              <Link to="/" className="hover:text-cyan-400 transition-colors">Home</Link>
              <a href="#" className="hover:text-cyan-400 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">Contact</a>
            </div>
            <div className="flex items-center gap-1 text-gray-500 text-xs uppercase tracking-widest">
              Made with <Heart size={10} className="text-red-500 fill-red-500" /> for Gamers
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}
